import {
  AutogiroApprovalState,
  AutogiroApprovalType,
  type BackOfficeAgreementCreationDtoV8,
  type BackOfficeAutogiroMandateResponseDtoV4,
  BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum,
  type BackOfficeDirectDebitNorwayMandateResponseDtoV4,
  type BackOfficeOverforselsServiceMandateResponseDtoV4,
  BackOfficeOverforselsServiceMandateResponseDtoV4AllOfProviderEnum,
  type BackOfficePeriodV8,
  type BackOfficeSepaDirectDebitMandateResponseDtoV4,
  type BackOfficeSignatoryDto,
  type CreateDirectDebitMandateV4Request,
  type GetDirectDebitMandatesV4200ResponseInner,
} from 'api/swagger/definitions/backoffice'

export const mapDirectDebitMandate = (directDebitMandate: GetDirectDebitMandatesV4200ResponseInner): DirectDebitMandate => {
  switch (directDebitMandate.type) {
    case 'AUTOGIRO':
      return mapDirectDebitAutoGiro(directDebitMandate)
    case 'OVERFORSELSSERVICE':
      return mapDirectDebitOverforselService(directDebitMandate)
    case 'SEPA_DIRECT_DEBIT':
      return mapDirectDebitSepa(directDebitMandate)
    case 'DIRECT_DEBIT_NORWAY':
      return mapDirectDebitNorway(directDebitMandate)
  }
}

export enum DirectDebitType {
  AUTOGIRO = 'AUTOGIRO',
  OVERFORSELSSERVICE = 'OVERFORSELSSERVICE',
  SEPA_DIRECT_DEBIT = 'SEPA_DIRECT_DEBIT',
  DIRECT_DEBIT_AGREEMENT = 'DIRECT_DEBIT_AGREEMENT',
  DIRECT_DEBIT_NORWAY = 'DIRECT_DEBIT_NORWAY',
}

export enum ApprovalState {
  REQUESTED = 'REQUESTED',
  PENDING = 'PENDING',
  READY = 'READY',
  APPROVED = 'APPROVED',
  FAILED = 'FAILED',
  PENDING_CANCELLATION = 'PENDING_CANCELLATION',
  CANCELLED = 'CANCELLED',
}

const mapApprovalState = (state: AutogiroApprovalState): ApprovalState => {
  switch (state) {
    case AutogiroApprovalState.REQUESTED:
      return ApprovalState.REQUESTED
    case AutogiroApprovalState.PENDING:
      return ApprovalState.PENDING
    case AutogiroApprovalState.READY:
      return ApprovalState.READY
    case AutogiroApprovalState.APPROVED:
      return ApprovalState.APPROVED
    case AutogiroApprovalState.FAILED:
      return ApprovalState.FAILED
    case AutogiroApprovalState.PENDING_CANCELLATION:
      return ApprovalState.PENDING_CANCELLATION
    case AutogiroApprovalState.CANCELLED:
      return ApprovalState.CANCELLED
  }
}

export enum DirectDebitProvider {
  ATLAR = 'ATLAR',
  BILLECTA = 'BILLECTA',
  MYNT = 'MYNT',
}

const mapDirectDebitProvider = (
  provider: BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum | BackOfficeOverforselsServiceMandateResponseDtoV4AllOfProviderEnum,
): DirectDebitProvider => {
  switch (provider) {
    case BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum.ATLAR:
      return DirectDebitProvider.ATLAR
    case BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum.BILLECTA:
      return DirectDebitProvider.BILLECTA
    case BackOfficeOverforselsServiceMandateResponseDtoV4AllOfProviderEnum.MYNT:
      return DirectDebitProvider.MYNT
  }
}

export interface DirectDebitAutoGiro {
  id: string
  createdAt: string
  modifiedAt: string
  state: ApprovalState
  provider: DirectDebitProvider
  log: string
  bankAccount: string
  type: DirectDebitType.AUTOGIRO
}

const mapDirectDebitAutoGiro = (autogiro: BackOfficeAutogiroMandateResponseDtoV4): DirectDebitAutoGiro => ({
  bankAccount: autogiro.bankAccount!,
  createdAt: autogiro.createdAt!,
  id: autogiro.id!,
  log: autogiro.log!,
  modifiedAt: autogiro.modifiedAt!,
  provider: mapDirectDebitProvider(autogiro.provider!),
  state: mapApprovalState(autogiro.state!),
  type: DirectDebitType.AUTOGIRO,
})

export interface DirectDebitOverforselService {
  id: string
  createdAt: string
  modifiedAt: string
  state: ApprovalState
  provider: DirectDebitProvider
  log: string
  bankAccount: string
  type: DirectDebitType.OVERFORSELSSERVICE
  contactInfo?: {
    fullName: string
    email: string
    phoneNumber: string
  }
}

const mapDirectDebitOverforselService = (
  overforselsService: BackOfficeOverforselsServiceMandateResponseDtoV4,
): DirectDebitOverforselService => ({
  bankAccount: overforselsService.bankAccount!,
  createdAt: overforselsService.createdAt!,
  id: overforselsService.id!,
  log: overforselsService.log!,
  modifiedAt: overforselsService.modifiedAt!,
  state: mapApprovalState(overforselsService.state!),
  provider: mapDirectDebitProvider(overforselsService.provider!),
  type: DirectDebitType.OVERFORSELSSERVICE,
  contactInfo: overforselsService.contactInfo
    ? {
        fullName: overforselsService.contactInfo.fullName,
        email: overforselsService.contactInfo.email,
        phoneNumber: overforselsService.contactInfo.phoneNumber,
      }
    : undefined,
})

export interface DirectDebitNorway {
  id: string
  createdAt: string
  modifiedAt: string
  state: ApprovalState
  provider: DirectDebitProvider
  log: string
  noBankAccount: string
  type: DirectDebitType.DIRECT_DEBIT_NORWAY
}

const mapDirectDebitNorway = (directDebitNorway: BackOfficeDirectDebitNorwayMandateResponseDtoV4): DirectDebitNorway => ({
  noBankAccount: directDebitNorway.bankAccount!,
  createdAt: directDebitNorway.createdAt!,
  id: directDebitNorway.id!,
  log: directDebitNorway.log!,
  modifiedAt: directDebitNorway.modifiedAt!,
  state: mapApprovalState(directDebitNorway.state!),
  provider: mapDirectDebitProvider(directDebitNorway.provider!),
  type: DirectDebitType.DIRECT_DEBIT_NORWAY,
})

export interface DirectDebitSepa {
  id: string
  createdAt: string
  modifiedAt: string
  state: ApprovalState
  provider: DirectDebitProvider
  log: string
  iban: string
  mandateReference: string
  signatureDate: string
  type: DirectDebitType.SEPA_DIRECT_DEBIT
}

const mapDirectDebitSepa = (sepa: BackOfficeSepaDirectDebitMandateResponseDtoV4): DirectDebitSepa => ({
  createdAt: sepa.createdAt!,
  id: sepa.id!,
  log: sepa.log!,
  modifiedAt: sepa.modifiedAt!,
  state: mapApprovalState(sepa.state!),
  provider: mapDirectDebitProvider(sepa.provider!),
  iban: sepa.iban!,
  mandateReference: sepa.mandateReference!,
  signatureDate: sepa.signatureDate!,
  type: DirectDebitType.SEPA_DIRECT_DEBIT,
})

export interface CreateDirectDebitAutogiro {
  bankAccount: string
  type: DirectDebitType.AUTOGIRO
}

export const mapCreateDirectDebitAutogiro = (mandate: CreateDirectDebitAutogiro): CreateDirectDebitMandateV4Request => ({
  bankAccount: mandate.bankAccount,
  type: AutogiroApprovalType.AUTOGIRO,
})

export type CreateDirectDebitAgreement =
  | {
      iban: string
      bankAccount?: undefined
      type: DirectDebitType.DIRECT_DEBIT_AGREEMENT
    }
  | {
      iban?: undefined
      bankAccount: string
      type: DirectDebitType.DIRECT_DEBIT_AGREEMENT
    }

export type CreateNorwegianDirectDebitAgreement = {
  noBankAccount: string
  signer: BackOfficeSignatoryDto
  type: DirectDebitType.DIRECT_DEBIT_NORWAY
  amount: number
  period: BackOfficePeriodV8
}

export const mapCreateDirectDebitAgreement = (
  mandate: CreateDirectDebitAgreement | CreateNorwegianDirectDebitAgreement,
): BackOfficeAgreementCreationDtoV8 => {
  if (mandate.type === DirectDebitType.DIRECT_DEBIT_NORWAY) {
    const signer = mandate.signer
    if (
      !signer.ssn.identifier ||
      signer.ssn.country === 'NL' ||
      signer.ssn.country === 'EE' ||
      signer.ssn.country === 'LV' ||
      signer.ssn.country === 'DE'
    ) {
      throw new Error('Missing or invalid SSN')
    }
    return {
      directDebitAgreement: {
        noBankAccount: mandate.noBankAccount,
        signer: {
          email: signer.email,
          fullName: signer.fullName,
          ssn: {
            ssn: signer.ssn.identifier!,
            nationality: signer.ssn.country!,
          },
          phoneNumber: signer.phoneNumber,
        },
        amountLimit: {
          amount: mandate.amount,
          period: mandate.period,
        },
        bankAccount: mandate.noBankAccount,
        type: 'NORWEGIAN_DIRECT_DEBIT_AGREEMENT',
      },
    }
  }
  if (mandate.iban) {
    return {
      directDebitAgreement: {
        iban: mandate.iban,
        type: 'FINNISH_DIRECT_DEBIT_AGREEMENT',
      },
    }
  }

  return {
    directDebitAgreement: {
      bankAccount: mandate.bankAccount,
      type: 'SWEDISH_DIRECT_DEBIT_AGREEMENT',
    },
  }
}

export interface CreateSepaDirectDebit {
  iban: string
  type: DirectDebitType.SEPA_DIRECT_DEBIT
}

export const mapCreateSepaDirectDebit = (mandate: CreateSepaDirectDebit): CreateDirectDebitMandateV4Request => ({
  iban: mandate.iban,
  type: AutogiroApprovalType.SEPA_DIRECT_DEBIT,
})

export interface CreateDirectDebitOverforselService {
  bankAccount: string
  contactInfo: {
    fullName: string
    email: string
    phoneNumber: string
  }
  type: DirectDebitType.OVERFORSELSSERVICE
}

export const mapCreateDirectDebitOverforselService = (mandate: CreateDirectDebitOverforselService): CreateDirectDebitMandateV4Request => ({
  dkBankAccount: mandate.bankAccount,
  contactInfo: {
    email: mandate.contactInfo.email,
    fullName: mandate.contactInfo.fullName,
    phoneNumber: mandate.contactInfo.phoneNumber,
  },
  type: AutogiroApprovalType.OVERFORSELSSERVICE,
})

export const mapCreateAgreementV8Request = (
  mandate: CreateDirectDebitAgreement | CreateNorwegianDirectDebitAgreement,
): BackOfficeAgreementCreationDtoV8 => mapCreateDirectDebitAgreement(mandate)

export const mapCreateDirectDebitMandateV4Request = (
  mandate: CreateDirectDebitAutogiro | CreateDirectDebitOverforselService | CreateSepaDirectDebit,
): CreateDirectDebitMandateV4Request => {
  switch (mandate.type) {
    case DirectDebitType.AUTOGIRO:
      return mapCreateDirectDebitAutogiro(mandate)
    case DirectDebitType.OVERFORSELSSERVICE:
      return mapCreateDirectDebitOverforselService(mandate)
    case DirectDebitType.SEPA_DIRECT_DEBIT:
      return mapCreateSepaDirectDebit(mandate)
  }
}

interface BaseMandate {
  type: DirectDebitType
}

export type DirectDebitMandate = DirectDebitAutoGiro | DirectDebitOverforselService | DirectDebitSepa | DirectDebitNorway

export const isDirectDebitAutoGiro = (mandate: BaseMandate): mandate is DirectDebitAutoGiro => mandate.type === DirectDebitType.AUTOGIRO

export const isDirectDebitOverforselService = (mandate: BaseMandate): mandate is DirectDebitOverforselService =>
  mandate.type === DirectDebitType.OVERFORSELSSERVICE

export const isDirectDebitSepa = (mandate: BaseMandate): mandate is DirectDebitSepa => mandate.type === DirectDebitType.SEPA_DIRECT_DEBIT
